import { SpanStatusCode, trace } from "@opentelemetry/api";

/**
 * Structured server-side error logger for Datadog Error Tracking.
 *
 * Outputs JSON with `error.kind`, `error.message`, and `error.stack` so
 * Datadog's log pipeline can route entries into Error Tracking.
 * Also records the exception on the active OTel span (if any) so the
 * error appears in APM Error Tracking with full trace context.
 *
 * Includes Datadog unified service tags (`dd.service`, `dd.env`, `dd.version`)
 * and git metadata (`git.commit.sha`, `git.repository_url`) so Error Tracking
 * can display env/version info and link stack traces to GitHub source code.
 *
 * Usage:
 *   import { logError, logWarn } from "@/lib/server-logger";
 *
 *   try { ... } catch (error) {
 *     logError("Failed to create payment intent", error, { consumerId });
 *   }
 */

const ddTags = {
  "dd.service": "sam",
  "dd.env": process.env.SAM_ENV === "production" ? "prod" : process.env.SAM_ENV,
  "dd.version": process.env.VERCEL_GIT_COMMIT_SHA?.slice(0, 7) || "local",
  "git.commit.sha": process.env.VERCEL_GIT_COMMIT_SHA,
  "git.repository_url": "github.com/rsup2/frontend",
};

function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  return new Error(typeof value === "string" ? value : JSON.stringify(value));
}

export function logError(
  message: string,
  error?: unknown,
  context?: Record<string, unknown>,
) {
  const err = error != null ? toError(error) : new Error(message);

  // Path A: structured log → Logs Error Tracking
  console.error(
    JSON.stringify({
      message,
      status: "error",
      ...ddTags,
      error: {
        kind: err.name,
        message: err.message,
        stack: err.stack,
      },
      ...(context && { context }),
    }),
  );

  // Path B: span exception → APM Error Tracking
  const span = trace.getActiveSpan();
  if (span) {
    span.recordException(err);
    span.setStatus({ code: SpanStatusCode.ERROR, message: err.message });
  }
}

/**
 * Structured warning for non-fatal issues (bad data, missing fields).
 * These go to Datadog Logs but NOT Error Tracking (status: "warn").
 */
export function logWarn(message: string, context?: Record<string, unknown>) {
  console.warn(
    JSON.stringify({
      message,
      status: "warn",
      ...ddTags,
      ...(context && { context }),
    }),
  );
}
